/**
 * CLI handler for syncing a workflow's DAG (nodes + edges) atomically.
 *
 * Reads the new state from a JSON file or stdin, creates a draft snapshot
 * of the current state for safety, then replaces all nodes and edges.
 *
 * Input is flexible — the CLI auto-fills missing fields:
 *   - Node `id` and `orderIndex` are auto-generated
 *   - `edges` array is optional — linear sequential edges are created
 *   - Accepts the same minimal format as `canary workflow create`
 *
 * Usage:
 *   canary workflow sync <workflowId> --from-file <path>
 *   cat state.json | canary workflow sync <workflowId> --from-stdin
 *
 * Minimal input: { "nodes": [{ "nodeType": "login", "configJson": {} }, ...] }
 *
 * @module WorkflowSyncCLI
 */

import crypto from 'node:crypto';
import fs from 'node:fs/promises';
import process from 'node:process';
import { alignSnapshotNodeIds, type AlignNode } from '@chatsdet/workflow-diff';
import { normalizeNodeType } from '@chatsdet/types';
import { getArgValue, hasFlag } from './auth.js';
import { apiRequest } from './cli-helpers.js';
import { readFromStdin } from './cli-format.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SyncResponse = {
  ok: boolean;
  error?: string;
  snapshotVersion?: string;
};

type ProposalResponse = {
  ok: boolean;
  error?: string;
  data?: {
    id: string;
    title: string;
    status: string;
  };
};

// ---------------------------------------------------------------------------
// Auto-fill helpers
// ---------------------------------------------------------------------------

type RawNode = Record<string, unknown>;
type RawEdge = Record<string, unknown>;

function normalizeNodesAndEdges(
  rawNodes: RawNode[],
  rawEdges?: RawEdge[]
): { nodes: RawNode[]; edges: RawEdge[] } {
  // Fill missing node fields with defaults
  const nodes = rawNodes.map((node, i) => ({
    ...node,
    id: (node.id as string) ?? crypto.randomUUID(),
    orderIndex: (node.orderIndex as number) ?? i,
    configJson: (node.configJson as Record<string, unknown>) ?? {},
  }));

  // If edges provided, just fill missing IDs
  if (rawEdges && rawEdges.length > 0) {
    const edges = rawEdges.map((edge) => ({
      ...edge,
      id: (edge.id as string) ?? crypto.randomUUID(),
    }));
    return { nodes, edges };
  }

  // Auto-generate linear sequential edges
  const edges: RawEdge[] = [];
  for (let i = 0; i < nodes.length - 1; i++) {
    edges.push({
      id: crypto.randomUUID(),
      sourceNodeId: nodes[i].id,
      targetNodeId: nodes[i + 1].id,
      edgeType: 'default',
    });
  }

  return { nodes, edges };
}

// ---------------------------------------------------------------------------
// Input reading
// ---------------------------------------------------------------------------

async function readSyncInput(argv: string[]): Promise<{ nodes: RawNode[]; edges?: RawEdge[] }> {
  const filePath = getArgValue(argv, '--from-file');
  const fromStdin = hasFlag(argv, '--from-stdin');

  let raw: string;
  if (filePath) {
    try {
      raw = await fs.readFile(filePath, 'utf-8');
    } catch (err) {
      console.error(`Error reading file: ${err instanceof Error ? err.message : String(err)}`);
      process.exit(1);
    }
  } else if (fromStdin) {
    try {
      raw = await readFromStdin();
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err));
      process.exit(1);
    }
  } else {
    console.error('Error: Must provide --from-file <path> or --from-stdin');
    process.exit(1);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    console.error('Error: Invalid JSON input');
    process.exit(1);
  }

  const obj = parsed as Record<string, unknown>;

  // Normalize "acting-as" → "login" on user-supplied node entries so callers
  // can use the new spelling without breaking the canonical persisted type.
  const normalizeNodes = (raw: unknown[]): RawNode[] => {
    return raw.map((entry) => {
      if (entry && typeof entry === 'object' && 'nodeType' in entry) {
        const node = entry as RawNode;
        if (typeof node.nodeType === 'string') {
          node.nodeType = normalizeNodeType(node.nodeType);
        }
      }
      return entry as RawNode;
    });
  };

  // Direct format: { nodes: [...], edges?: [...] }
  if (Array.isArray(obj.nodes)) {
    return {
      nodes: normalizeNodes(obj.nodes),
      edges: Array.isArray(obj.edges) ? obj.edges : undefined,
    };
  }

  // Wrapped format from `canary workflow get`: { workflow: { nodes: [...], edges: [...] } }
  const workflow = obj.workflow as Record<string, unknown> | undefined;
  if (workflow && Array.isArray(workflow.nodes)) {
    return {
      nodes: normalizeNodes(workflow.nodes),
      edges: Array.isArray(workflow.edges) ? workflow.edges : undefined,
    };
  }

  console.error('Error: JSON must contain a "nodes" array.');
  console.error('Accepted formats:');
  console.error('  { "nodes": [{ "nodeType": "login", "configJson": {} }, ...] }');
  console.error('  { "nodes": [...], "edges": [...] }');
  console.error('  { "workflow": { "nodes": [...], "edges": [...] } }');
  console.error('');
  console.error('Edges, IDs, positions, and ordering are auto-generated when omitted.');
  process.exit(1);
}

// ---------------------------------------------------------------------------
// Current-workflow ID alignment (for --propose)
// ---------------------------------------------------------------------------

type WorkflowGetResponse = {
  ok: boolean;
  error?: string;
  workflow?: {
    id: string;
    nodes?: Array<{
      id: string;
      nodeType: string;
      orderIndex?: number;
      configJson?: Record<string, unknown> | null;
    }>;
  };
};

async function alignWithCurrentWorkflow(
  apiUrl: string,
  token: string,
  workflowId: string,
  nodes: RawNode[],
  edges: RawEdge[],
): Promise<{ nodes: RawNode[]; edges: RawEdge[] }> {
  // Best-effort: a failed fetch (network blip, 404, permissions) shouldn't
  // block the proposal — the server-side aligner will still run.
  let currentNodes: AlignNode[] = [];
  try {
    const current = await apiRequest<WorkflowGetResponse>(
      apiUrl,
      token,
      'GET',
      `/workflows/${workflowId}`,
    );
    if (current.ok && current.workflow?.nodes) {
      currentNodes = current.workflow.nodes.map((n) => ({
        id: n.id,
        nodeType: n.nodeType,
        orderIndex: n.orderIndex,
        configJson: n.configJson ?? {},
      }));
    }
  } catch {
    // Swallow — alignment is a best-effort optimization on the client.
  }

  if (currentNodes.length === 0) {
    return { nodes, edges };
  }

  const proposedSnapshot = {
    nodes: nodes.map((n) => ({
      id: String(n.id),
      nodeType: String(n.nodeType ?? ''),
      orderIndex: typeof n.orderIndex === 'number' ? n.orderIndex : undefined,
      configJson: (n.configJson as Record<string, unknown> | null | undefined) ?? {},
    })),
    edges: edges.map((e) => ({
      id: String(e.id),
      sourceNodeId: String(e.sourceNodeId),
      targetNodeId: String(e.targetNodeId),
      edgeType: (e.edgeType as string | null | undefined) ?? null,
    })),
  };

  const { snapshot } = alignSnapshotNodeIds(proposedSnapshot, currentNodes);

  // Splice aligned IDs back onto the original raw nodes/edges so any extra
  // fields callers passed through (canonicalPageId, etc.) are preserved.
  const alignedNodes: RawNode[] = nodes.map((n, i) => ({
    ...n,
    id: snapshot.nodes[i]!.id,
  }));
  const alignedEdges: RawEdge[] = edges.map((e, i) => ({
    ...e,
    sourceNodeId: snapshot.edges[i]!.sourceNodeId,
    targetNodeId: snapshot.edges[i]!.targetNodeId,
  }));

  return { nodes: alignedNodes, edges: alignedEdges };
}

// ---------------------------------------------------------------------------
// Handler
// ---------------------------------------------------------------------------

export async function handleWorkflowSync(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  if (!workflowId) {
    console.error('Error: Missing workflow ID.');
    console.error('Usage: canary workflow sync <workflowId> --from-file <path>');
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, '--json');
  const noSnapshot = hasFlag(argv, '--no-snapshot');
  const propose = hasFlag(argv, '--propose');
  const proposalTitle = getArgValue(argv, '--title');
  const proposalDescription = getArgValue(argv, '--description');

  const raw = await readSyncInput(argv);
  const { nodes, edges } = normalizeNodesAndEdges(raw.nodes, raw.edges);

  // --propose: create a proposal instead of applying directly
  if (propose) {
    const title = proposalTitle ?? `CLI sync proposal (${nodes.length} nodes)`;

    // Reuse existing workflow node IDs whenever a proposed step lines up with
    // a current step (same nodeType + orderIndex). Without this the diff
    // engine — which matches by node.id — sees the proposal as a wholesale
    // rewrite even when only a few configs changed. The server-side aligner
    // is the authoritative pass; doing it here too keeps CLI output sensible
    // and gives agents a clean diff even before they round-trip through the
    // API.
    const aligned = await alignWithCurrentWorkflow(apiUrl, token, workflowId, nodes, edges);

    const result = await apiRequest<ProposalResponse>(
      apiUrl,
      token,
      'POST',
      `/workflows/${workflowId}/proposals`,
      {
        title,
        description: proposalDescription ?? null,
        snapshotJson: { nodes: aligned.nodes, edges: aligned.edges },
        source: 'api',
      }
    );

    if (!result.ok) {
      if (jsonOutput) {
        console.log(JSON.stringify({ ok: false, error: result.error ?? 'Unknown error' }, null, 2));
      } else {
        console.error(`Error: ${result.error ?? 'Unknown error'}`);
      }
      process.exit(1);
    }

    if (jsonOutput) {
      console.log(JSON.stringify(result, null, 2));
      return;
    }

    console.log(`Proposal created for workflow ${workflowId} (${nodes.length} nodes, ${edges.length} edges).`);
    if (result.data?.id) {
      console.log(`Proposal ID: ${result.data.id}`);
    }
    return;
  }

  const result = await apiRequest<SyncResponse>(
    apiUrl,
    token,
    'PUT',
    `/workflows/${workflowId}/draft-state`,
    { nodes, edges, createSnapshot: !noSnapshot }
  );

  if (!result.ok) {
    if (jsonOutput) {
      console.log(JSON.stringify({ ok: false, error: result.error ?? 'Unknown error' }, null, 2));
    } else {
      console.error(`Error: ${result.error ?? 'Unknown error'}`);
    }
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  console.log(`Workflow ${workflowId} synced (${nodes.length} nodes, ${edges.length} edges).`);
  if (result.snapshotVersion) {
    console.log(`Draft snapshot created: ${result.snapshotVersion}`);
  }
}
